import { login, useAuthContext } from "../../auth";
import { useRedirectPath } from "../../utils/hooks";
import { FaEye, FaEyeSlash, FaUser, FaLock, FaArrowRight } from "react-icons/fa";
import axios from "axios";
import { useState } from "react";
import toast from "react-hot-toast";
import { Navigate, useNavigate } from "react-router-dom";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const redirectPath = useRedirectPath("/dashboard");
  const { isLoggedIn, isGuest } = useAuthContext();
  const navigate = useNavigate();

  if (isLoggedIn && !isGuest) {
    return <Navigate to={redirectPath} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success("Login successful!");
      navigate(redirectPath);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Incorrect email or password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse [animation-delay:2s]" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse [animation-delay:4s]" />
        </div>

        <div className="relative flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div>
            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center p-2 mb-12">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 64 64"
                fill="none"
              >
                <path
                  d="M49.9503 45.8831L37.0044 15.4464C36.8289 15.0335 36.4943 14.707 36.0814 14.5127C35.8142 14.3885 35.5119 14.3184 35.1989 14.3184H28.9162C28.6031 14.3184 28.3035 14.3885 28.0391 14.5127C28.0418 14.5235 28.0445 14.537 28.0499 14.5478L32.0602 23.9772L40.7745 44.469C40.9715 44.9332 41.4007 45.2543 41.9026 45.3191C44.5339 45.6564 46.9952 46.126 49.2325 46.7062C49.7236 46.8331 50.1474 46.3473 49.9503 45.8831Z"
                  fill="rgba(255,255,255,0.9)"
                />
                <path
                  d="M35.2016 14.3184H28.9162C28.6031 14.3184 28.3035 14.3885 28.0391 14.51C28.0418 14.5208 28.0445 14.5343 28.0499 14.5451L32.0602 23.9476L36.0841 14.5127C35.8142 14.3885 35.5146 14.3184 35.2016 14.3184Z"
                  fill="rgba(255,255,255,0.7)"
                />
                <path
                  d="M35.2021 14.3184H28.9166C28.6036 14.3184 28.304 14.3885 28.0395 14.51C27.6239 14.7016 27.2893 15.0281 27.1112 15.441L14.0356 46.0936C13.8926 46.431 14.203 46.7845 14.5592 46.69C16.9098 46.0639 19.5168 45.562 22.3101 45.2111C22.7527 45.1545 23.1332 44.8684 23.3086 44.4609L32.0607 23.9449L36.0846 14.51C35.8147 14.3885 35.5151 14.3184 35.2021 14.3184Z"
                  fill="rgba(255,255,255,0.8)"
                />
              </svg>
            </div>

            <h1 className="text-[48px] font-semibold leading-tight mb-6">
              Health Promotion
              <br />
              Building Dashboard
            </h1>
            <p className="text-[18px] font-normal leading-[27px] text-slate-300">
              A dashboard that aggregates building performance data
              for real-time monitoring and energy optimization.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
              <svg className="w-6 h-6 text-white mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-[16px] font-semibold leading-[24px] text-white mb-1">Energy Consumption</h3>
              <p className="text-[12px] font-normal leading-[18px] text-slate-300">Track electricity, water, and carbon footprint in real time</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
              <svg className="w-6 h-6 text-white mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="text-[16px] font-semibold leading-[24px] text-white mb-1">Occupancy Insights</h3>
              <p className="text-[12px] font-normal leading-[18px] text-slate-300">Monitor building occupancy and people flow patterns</p>
            </div>
          </div>

          {/* Footer */}
          <p className="text-[14px] font-normal leading-[21px] text-slate-400">
            Powered by AltoTech
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-slate-100 to-slate-200">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {/* Form Header */}
          <div className="text-center">
            <h2 className="text-[32px] font-semibold leading-[48px] tracking-[0.003em] text-gray-900 mb-2">Welcome back</h2>
            <p className="text-[16px] font-normal leading-[24px] tracking-[-0.002em] text-gray-600">Sign in to continue to your dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-[16px] font-semibold leading-[24px] tracking-[0.0005em] text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser size={18} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 focus:bg-white text-base"
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-[16px] font-semibold leading-[24px] tracking-[0.0005em] text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock size={18} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  type={isPasswordVisible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 focus:bg-white text-base"
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {isPasswordVisible ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                </button>
              </div>
            </div>

            {/* TODO: Forgot password - hidden until page is implemented */}
            {/* <div className="flex justify-end">
              <span
                className="text-sm text-primary hover:text-primary-dark cursor-pointer hover:underline"
                onClick={() => navigate("/forget-password")}
              >
                Forgot password?
              </span>
            </div> */}

            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="text-[14px] font-medium leading-[21px]">Signing in...</span>
                </>
              ) : (
                <>
                  <span className="text-[14px] font-medium leading-[21px]">Sign in</span>
                  <FaArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* TODO: Sign Up - hidden for now */}
          {/* <div className="mt-6 text-center">
            <span className="text-gray-500 text-sm">Don't have an account? </span>
            <span
              className="text-sm font-semibold text-primary-dark hover:text-primary cursor-pointer hover:underline"
              onClick={() => navigate("/sign-up")}
            >
              Sign Up
            </span>
          </div> */}
        </div>
      </div>
    </div>
  );
}
